// components/admin/ProductManager.js - Complete Working with Cherry Theme & Full-Screen Image View
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ProductForm from './ProductForm';
import { toast } from 'react-toastify';
import { dbGet, dbSet, dbUpdate } from '../../config/firebase';
import { 
  Plus, Search, X, Edit3, Trash2, Package, 
  Tag, Grid, List, Layers, FolderTree, Folder,
  Edit, Trash, AlertCircle, Clock, TrendingUp,
  ChevronLeft, ChevronRight, Maximize2, X as XIcon
} from 'lucide-react';

// Full-Screen Image Viewer Component (same as ProductDetailPage)
const FullScreenImageViewer = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const minSwipeDistance = 50;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex]);

  const goPrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const goNext = () => {
    if (currentIndex < images.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const onTouchStartHandler = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMoveHandler = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEndHandler = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) goNext();
      else goPrev();
    }
    setTouchStart(null);
    setTouchEnd(null);
  };

  const currentImage = images[currentIndex];

  // Collect all images from product and its variants
  const getAllImages = () => {
    const allImgs = [];
    
    // Add main product image
    if (currentImage) {
      allImgs.push({ url: currentImage, type: 'main' });
    }
    
    // Add variant images
    if (images.length > 1) {
      images.forEach((img, idx) => {
        if (idx !== initialIndex && img) {
          allImgs.push({ url: img, type: 'variant' });
        }
      });
    }
    
    return allImgs;
  };

  const allImagesList = getAllImages();

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.95)',
        zIndex: 5000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        touchAction: 'none'
      }}
      onTouchStart={onTouchStartHandler}
      onTouchMove={onTouchMoveHandler}
      onTouchEnd={onTouchEndHandler}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.15)',
          border: 'none',
          color: 'white',
          cursor: 'pointer',
          zIndex: 5002,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(10px)',
          transition: 'background 0.2s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
      >
        <XIcon size={24} />
      </button>

      {/* Image Counter */}
      {allImagesList.length > 1 && (
        <div style={{
          position: 'absolute',
          top: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.6)',
          color: 'white',
          padding: '4px 14px',
          borderRadius: 20,
          fontSize: isMobile ? '12px' : '14px',
          backdropFilter: 'blur(10px)',
          zIndex: 5002
        }}>
          {currentIndex + 1} / {allImagesList.length}
        </div>
      )}

      {/* Main Image */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '60px 10px 80px' : '70px 20px 100px'
      }}>
        <img
          src={allImagesList[currentIndex]?.url}
          alt={`Slide ${currentIndex + 1}`}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            userSelect: 'none',
            WebkitUserSelect: 'none'
          }}
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=400&q=80';
          }}
        />

        {/* Navigation Arrows - Desktop only */}
        {allImagesList.length > 1 && !isMobile && (
          <>
            {currentIndex > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                style={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(10px)',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
              >
                <ChevronLeft size={28} />
              </button>
            )}
            {currentIndex < allImagesList.length - 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                style={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(10px)',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
              >
                <ChevronRight size={28} />
              </button>
            )}
          </>
        )}

        {/* Image Counter Bottom */}
        {allImagesList.length > 1 && (
          <div style={{
            position: 'absolute',
            bottom: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(0,0,0,0.6)',
            color: 'white',
            padding: '4px 14px',
            borderRadius: 20,
            fontSize: isMobile ? '12px' : '14px',
            backdropFilter: 'blur(10px)',
            zIndex: 5002
          }}>
            {currentIndex + 1} / {allImagesList.length}
          </div>
        )}
      </div>

      {/* Thumbnails - Bottom */}
      {allImagesList.length > 1 && (
        <div style={{
          position: 'absolute',
          bottom: 60,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: isMobile ? '8px' : '12px',
          padding: '0 16px',
          overflowX: 'auto',
          zIndex: 5002,
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
        className="hide-scrollbar"
        >
          {allImagesList.map((img, idx) => {
            const isActive = idx === currentIndex;
            return (
              <div
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                style={{
                  width: isMobile ? '44px' : '60px',
                  height: isMobile ? '44px' : '60px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: isActive ? '3px solid #dc2626' : '2px solid rgba(255,255,255,0.3)',
                  cursor: 'pointer',
                  flexShrink: 0,
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)';
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
                }}
              >
                <img
                  src={img.url}
                  alt=""
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=100&q=80';
                  }}
                />
                {img.type === 'variant' && (
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'rgba(220,38,38,0.8)',
                    padding: '1px 4px',
                    fontSize: '6px',
                    color: 'white',
                    textAlign: 'center',
                    fontWeight: 600,
                    textTransform: 'uppercase'
                  }}>
                    Variant
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Mobile Swipe Hint */}
      {isMobile && allImagesList.length > 1 && (
        <div style={{
          position: 'absolute',
          bottom: 130,
          left: '50%',
          transform: 'translateX(-50%)',
          color: 'rgba(255,255,255,0.4)',
          fontSize: '11px',
          zIndex: 5002,
          textAlign: 'center',
          pointerEvents: 'none'
        }}>
          ← Swipe to navigate →
        </div>
      )}

      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

// Category Card Component - Cherry Theme
const CategoryCard = ({ category, isActive, onClick, productCount, onEdit, onDelete, isMobile }) => {
  if (!category) return null;
  
  return (
    <div style={{
      position: 'relative', textAlign: 'center', cursor: 'pointer', transition: 'all 0.3s ease',
      background: isActive ? 'linear-gradient(135deg, #dc2626, #b91c1c)' : 'white',
      borderRadius: '12px', padding: isMobile ? '12px 8px' : '16px 12px',
      border: isActive ? 'none' : '1px solid rgba(220,38,38,0.15)',
      boxShadow: isActive ? '0 8px 20px rgba(220,38,38,0.3)' : '0 2px 8px rgba(0,0,0,0.04)'
    }} onClick={() => onClick(category)}>
      <div style={{ 
        width: isMobile ? '45px' : '55px', 
        height: isMobile ? '45px' : '55px', 
        margin: '0 auto 8px', 
        borderRadius: '50%', 
        background: isActive ? 'rgba(255,255,255,0.2)' : '#fff5f5', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <span style={{ fontSize: isMobile ? '24px' : '28px' }}>{category.icon || '🌸'}</span>
      </div>
      <h3 style={{ 
        fontSize: isMobile ? '12px' : '13px', 
        fontWeight: 600, 
        color: isActive ? 'white' : '#1a0a0a', 
        marginBottom: '4px' 
      }}>{category.name}</h3>
      <p style={{ 
        fontSize: '11px', 
        color: isActive ? 'rgba(255,255,255,0.8)' : '#dc2626', 
        fontWeight: 600 
      }}>{productCount} Products</p>
      <div style={{ 
        position: 'absolute', 
        top: '6px', 
        right: '6px', 
        display: 'flex', 
        gap: '4px', 
        opacity: 0, 
        transition: 'opacity 0.2s' 
      }} className="category-actions" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => onEdit(category)} style={{ 
          width: '26px', height: '26px', borderRadius: '6px', background: 'white', 
          border: '1px solid #e2e8f0', cursor: 'pointer' 
        }}><Edit size={12} color="#64748b" /></button>
        <button onClick={() => onDelete(category)} style={{ 
          width: '26px', height: '26px', borderRadius: '6px', background: 'white', 
          border: '1px solid #e2e8f0', cursor: 'pointer' 
        }}><Trash size={12} color="#ef4444" /></button>
      </div>
    </div>
  );
};

// Subcategory Card - Cherry Theme
const SubCategoryCard = ({ subcategory, productCount, onEdit, onDelete, onAddProduct, isMobile }) => {
  if (!subcategory) return null;
  
  return (
    <div style={{ 
      background: '#fff5f5', 
      borderRadius: '10px', 
      padding: '10px', 
      border: '1px solid rgba(220,38,38,0.15)', 
      transition: 'all 0.2s ease' 
    }}
    onMouseEnter={(e) => { 
      e.currentTarget.style.background = '#fee2e2'; 
      e.currentTarget.style.transform = 'translateX(3px)'; 
    }}
    onMouseLeave={(e) => { 
      e.currentTarget.style.background = '#fff5f5'; 
      e.currentTarget.style.transform = 'translateX(0)'; 
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
          <Folder size={14} color="#dc2626" />
          <span style={{ fontWeight: 500, fontSize: '13px', color: '#1a0a0a' }}>{subcategory.name}</span>
        </div>
        <div style={{ display: 'flex', gap: '4px' }}>
          <button onClick={() => onAddProduct(subcategory)} style={{ 
            padding: '4px 10px', background: '#10b981', color: 'white', border: 'none', 
            borderRadius: '6px', fontSize: '10px', fontWeight: 500, cursor: 'pointer' 
          }}><Plus size={10} /> Add</button>
          <button onClick={() => onEdit(subcategory)} style={{ 
            width: '28px', height: '28px', borderRadius: '6px', background: 'white', 
            border: '1px solid #e2e8f0', cursor: 'pointer' 
          }}><Edit size={12} color="#64748b" /></button>
          <button onClick={() => onDelete(subcategory)} style={{ 
            width: '28px', height: '28px', borderRadius: '6px', background: 'white', 
            border: '1px solid #e2e8f0', cursor: 'pointer' 
          }}><Trash size={12} color="#ef4444" /></button>
        </div>
      </div>
      <p style={{ fontSize: '10px', color: '#dc2626', marginTop: '6px', marginLeft: '22px' }}>
        {productCount} products
      </p>
    </div>
  );
};

// Product Card for Admin - Cherry Theme Grid View with Full-Screen Image
const ProductCardAdmin = ({ product, onEdit, onDelete, onToggleStock, isMobile, onImageClick }) => {
  if (!product) return null;
  
  const discountPercent = product.oldPrice && product.oldPrice > product.price 
    ? Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100) 
    : 0;
  const isOutOfStock = product.stock === false || product.stock === 'false' || product.outOfStock === true;
  
  // Collect all images for full-screen view
  const getAllProductImages = () => {
    const images = [];
    if (product.image) images.push(product.image);
    if (product.variants) {
      product.variants.forEach(variant => {
        if (variant.images) {
          variant.images.forEach(img => {
            if (img.url) images.push(img.url);
          });
        }
      });
    }
    return images.length > 0 ? images : [product.image || 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=400&q=80'];
  };

  const productImages = getAllProductImages();

  const handleImageClick = (e) => {
    e.stopPropagation();
    if (onImageClick && productImages.length > 0) {
      onImageClick(productImages, 0);
    }
  };

  return (
    <div style={{ 
      background: 'white', 
      borderRadius: '12px', 
      overflow: 'hidden', 
      border: isOutOfStock ? '2px solid #ef4444' : '1px solid rgba(220,38,38,0.15)', 
      transition: 'all 0.3s ease',
      opacity: isOutOfStock ? 0.7 : 1
    }}
      onMouseEnter={(e) => { 
        e.currentTarget.style.transform = 'translateY(-4px)'; 
        e.currentTarget.style.boxShadow = '0 8px 20px rgba(220,38,38,0.15)'; 
      }}
      onMouseLeave={(e) => { 
        e.currentTarget.style.transform = 'translateY(0)'; 
        e.currentTarget.style.boxShadow = 'none'; 
      }}>
      <div 
        style={{ 
          position: 'relative', 
          height: isMobile ? '140px' : '160px', 
          background: '#fefaf8',
          cursor: 'pointer'
        }}
        onClick={handleImageClick}
      >
        <img 
          src={product.image} 
          alt={product.name} 
          style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
          onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=200&q=80'} 
        />
        
        {/* Full-screen icon overlay on hover */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.4)',
          opacity: 0,
          transition: 'opacity 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        className="image-overlay"
        >
          <div style={{
            background: 'rgba(0,0,0,0.7)',
            borderRadius: '50%',
            padding: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Maximize2 size={24} color="white" />
          </div>
          {productImages.length > 1 && (
            <div style={{
              position: 'absolute',
              bottom: '8px',
              right: '8px',
              background: 'rgba(0,0,0,0.6)',
              color: 'white',
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '10px'
            }}>
              +{productImages.length - 1} more
            </div>
          )}
        </div>

        {discountPercent > 0 && (
          <div style={{ 
            position: 'absolute', bottom: '8px', left: '8px', background: '#dc2626', 
            color: 'white', padding: '3px 8px', borderRadius: '12px', fontSize: '10px', 
            fontWeight: 700 
          }}>{discountPercent}% OFF</div>
        )}
        {product.variants?.length > 0 && (
          <div style={{ 
            position: 'absolute', top: '8px', right: '8px', background: '#2874f0', 
            color: 'white', padding: '3px 8px', borderRadius: '12px', fontSize: '9px', 
            fontWeight: 600 
          }}><Layers size={10} /> {product.variants.length}</div>
        )}
        {isOutOfStock && (
          <div style={{ 
            position: 'absolute', top: '8px', left: '8px', background: '#ef4444', 
            color: 'white', padding: '3px 8px', borderRadius: '12px', fontSize: '9px', 
            fontWeight: 600 
          }}>Out of Stock</div>
        )}
        {productImages.length > 1 && (
          <div style={{ 
            position: 'absolute', bottom: '8px', right: '8px', 
            background: 'rgba(0,0,0,0.6)', color: 'white', 
            padding: '2px 8px', borderRadius: '12px', fontSize: '9px',
            display: 'flex', alignItems: 'center', gap: '4px'
          }}>
            <Maximize2 size={10} /> {productImages.length}
          </div>
        )}
      </div>
      <div style={{ padding: '12px' }}>
        <h4 style={{ 
          fontSize: '13px', fontWeight: 600, marginBottom: '4px', 
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' 
        }}>{product.name}</h4>
        <div>
          <span style={{ fontSize: '14px', fontWeight: 700, color: '#dc2626' }}>
            ₹{(product.price || 0).toLocaleString()}
          </span>
          {product.oldPrice && (
            <span style={{ fontSize: '10px', color: '#94a3b8', textDecoration: 'line-through', marginLeft: '8px' }}>
              ₹{product.oldPrice.toLocaleString()}
            </span>
          )}
        </div>
        {product.tag && (
          <span style={{ 
            display: 'inline-block', padding: '2px 8px', background: '#fff5f5', 
            color: '#dc2626', borderRadius: '12px', fontSize: '9px', fontWeight: 500, 
            marginBottom: '6px' 
          }}>{product.tag}</span>
        )}
        <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
          <button onClick={() => onEdit(product)} style={{ 
            flex: 1, padding: '5px', background: '#e3f2fd', color: '#1565c0', 
            borderRadius: '6px', border: 'none', fontSize: '10px', fontWeight: 600, cursor: 'pointer' 
          }}><Edit3 size={10} /> Edit</button>
          <button onClick={() => onToggleStock(product)} style={{ 
            flex: 1, padding: '5px', background: isOutOfStock ? '#10b981' : '#fef2f2', 
            color: isOutOfStock ? 'white' : '#dc2626', borderRadius: '6px', border: 'none', 
            fontSize: '10px', fontWeight: 600, cursor: 'pointer' 
          }}>
            {isOutOfStock ? 'Restock' : 'Out of Stock'}
          </button>
          <button onClick={() => onDelete(product)} style={{ 
            padding: '5px 8px', background: '#fef2f2', color: '#dc2626', 
            borderRadius: '6px', border: 'none', cursor: 'pointer' 
          }}><Trash2 size={10} /></button>
        </div>
      </div>

      <style>{`
        .image-overlay {
          opacity: 0;
        }
        div:hover > .image-overlay {
          opacity: 1;
        }
      `}</style>
    </div>
  );
};

// Product Row for List View
const ProductRow = ({ product, index, onEdit, onDelete, onToggleStock, onImageClick }) => {
  if (!product) return null;
  
  const discountPercent = product.oldPrice && product.oldPrice > product.price 
    ? Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100) 
    : 0;
  const isOutOfStock = product.stock === false || product.stock === 'false' || product.outOfStock === true;
  
  // Collect all images
  const getAllProductImages = () => {
    const images = [];
    if (product.image) images.push(product.image);
    if (product.variants) {
      product.variants.forEach(variant => {
        if (variant.images) {
          variant.images.forEach(img => {
            if (img.url) images.push(img.url);
          });
        }
      });
    }
    return images.length > 0 ? images : [product.image || 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=400&q=80'];
  };

  const productImages = getAllProductImages();

  const handleImageClick = (e) => {
    e.stopPropagation();
    if (onImageClick && productImages.length > 0) {
      onImageClick(productImages, 0);
    }
  };

  return (
    <tr style={{ borderBottom: '1px solid #f1f5f9', background: isOutOfStock ? '#fef2f2' : 'white' }}>
      <td style={{ padding: '12px 8px', fontSize: '12px', textAlign: 'center' }}>{index + 1}</td>
      <td style={{ padding: '12px 8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div 
            style={{ 
              width: '40px', 
              height: '40px', 
              borderRadius: '6px', 
              overflow: 'hidden',
              cursor: 'pointer',
              position: 'relative',
              background: '#f8fafc'
            }}
            onClick={handleImageClick}
          >
            <img 
              src={product.image} 
              alt={product.name} 
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
              onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=200&q=80'} 
            />
            {productImages.length > 1 && (
              <div style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                background: 'rgba(0,0,0,0.6)',
                color: 'white',
                padding: '1px 4px',
                fontSize: '8px',
                borderRadius: '2px'
              }}>
                +{productImages.length - 1}
              </div>
            )}
          </div>
          <div>
            <div style={{ fontWeight: 500, fontSize: '13px' }}>{product.name}</div>
            {product.tag && (
              <span style={{ fontSize: '9px', color: '#dc2626', background: '#fff5f5', padding: '2px 6px', borderRadius: '10px' }}>
                {product.tag}
              </span>
            )}
          </div>
        </div>
      </td>
      <td style={{ padding: '12px 8px', fontSize: '13px', fontWeight: 600, color: '#dc2626' }}>
        ₹{(product.price || 0).toLocaleString()}
      </td>
      <td style={{ padding: '12px 8px', fontSize: '12px' }}>
        {product.oldPrice ? (
          <span>
            <span style={{ textDecoration: 'line-through', color: '#94a3b8' }}>₹{product.oldPrice.toLocaleString()}</span>
            <br/>
            <span style={{ color: '#10b981' }}>{discountPercent}% off</span>
          </span>
        ) : '-'}
      </td>
      <td style={{ padding: '12px 8px' }}>
        <span style={{ 
          padding: '4px 8px', borderRadius: '12px', fontSize: '10px', fontWeight: 600,
          background: isOutOfStock ? '#fef2f2' : '#ecfdf5',
          color: isOutOfStock ? '#dc2626' : '#10b981'
        }}>
          {isOutOfStock ? 'Out of Stock' : 'In Stock'}
        </span>
      </td>
      <td style={{ padding: '12px 8px' }}>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={() => onEdit(product)} style={{ 
            padding: '4px 8px', background: '#e3f2fd', color: '#1565c0', border: 'none', 
            borderRadius: '4px', cursor: 'pointer', fontSize: '11px' 
          }}>Edit</button>
          <button onClick={() => onToggleStock(product)} style={{ 
            padding: '4px 8px', background: isOutOfStock ? '#10b981' : '#fef2f2', 
            color: isOutOfStock ? 'white' : '#dc2626', border: 'none', borderRadius: '4px', 
            cursor: 'pointer', fontSize: '11px' 
          }}>
            {isOutOfStock ? 'Restock' : 'Out'}
          </button>
          <button onClick={() => onDelete(product)} style={{ 
            padding: '4px 8px', background: '#fef2f2', color: '#dc2626', border: 'none', 
            borderRadius: '4px', cursor: 'pointer', fontSize: '11px' 
          }}>Del</button>
        </div>
      </td>
    </tr>
  );
};

// Add Category Modal - Cherry Theme
const AddCategoryModal = ({ onClose, onSave, editingCategory }) => {
  const [name, setName] = useState(editingCategory?.name || '');
  const [icon, setIcon] = useState(editingCategory?.icon || '🌸');
  const [loading, setLoading] = useState(false);
  const iconOptions = ['🌸', '🌹', '🌺', '🌻', '🌼', '🌷', '💐', '🎁', '🎀', '🍰', '🧁', '🎂', '🍫', '🧸', '🪴', '🌿', '🍃', '📦', '✨', '⭐', '💝', '💖', '🎈', '🎉'];
  
  const handleSubmit = async () => {
    if (!name.trim()) { 
      toast.error('Category name required'); 
      return; 
    }
    setLoading(true);
    await onSave({ id: editingCategory?.id, name: name.trim(), icon });
    setLoading(false);
  };
  
  return (
    <>
      <div onClick={onClose} style={{ 
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, 
        backdropFilter: 'blur(2px)' 
      }} />
      <div style={{ 
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', 
        background: 'white', borderRadius: '16px', zIndex: 1101, width: '90%', 
        maxWidth: '400px', padding: '24px' 
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1a0a0a' }}>
            {editingCategory ? 'Edit Category' : 'Add New Category'}
          </h3>
          <button onClick={onClose} style={{ 
            width: '32px', height: '32px', borderRadius: '50%', background: '#f1f5f9', 
            border: 'none', cursor: 'pointer' 
          }}>✕</button>
        </div>
        <input 
          type="text" 
          value={name} 
          onChange={(e) => setName(e.target.value)} 
          placeholder="Category name" 
          style={{ 
            width: '100%', padding: '12px', marginBottom: '16px', border: '1px solid #e2e8f0', 
            borderRadius: '8px', fontSize: '14px' 
          }} 
          autoFocus 
        />
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '8px', color: '#334155' }}>
            Select Icon
          </label>
          <div style={{ 
            display: 'flex', flexWrap: 'wrap', gap: '8px', maxHeight: '120px', 
            overflowY: 'auto', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '8px' 
          }}>
            {iconOptions.map(opt => (
              <button 
                key={opt} 
                onClick={() => setIcon(opt)} 
                style={{ 
                  width: '40px', height: '40px', borderRadius: '8px', 
                  background: icon === opt ? '#dc2626' : '#f1f5f9', 
                  border: 'none', cursor: 'pointer', fontSize: '20px',
                  transition: 'all 0.2s'
                }}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={onClose} style={{ 
            flex: 1, padding: '10px', background: '#f1f5f9', borderRadius: '8px', 
            cursor: 'pointer', fontWeight: 500 
          }}>Cancel</button>
          <button onClick={handleSubmit} disabled={loading} style={{ 
            flex: 1, padding: '10px', background: loading ? '#94a3b8' : '#dc2626', 
            color: 'white', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer', 
            fontWeight: 600 
          }}>
            {loading ? 'Saving...' : (editingCategory ? 'Update' : 'Add')}
          </button>
        </div>
      </div>
    </>
  );
};

// Add Subcategory Modal - Cherry Theme
const AddSubcategoryModal = ({ onClose, onSave, categoryId, editingSubcategory }) => {
  const [name, setName] = useState(editingSubcategory?.name || '');
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async () => {
    if (!name.trim()) { 
      toast.error('Subcategory name required'); 
      return; 
    }
    setLoading(true);
    await onSave({ id: editingSubcategory?.id, name: name.trim(), categoryId });
    setLoading(false);
  };
  
  return (
    <>
      <div onClick={onClose} style={{ 
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, 
        backdropFilter: 'blur(2px)' 
      }} />
      <div style={{ 
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', 
        background: 'white', borderRadius: '16px', zIndex: 1101, width: '90%', 
        maxWidth: '380px', padding: '24px' 
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1a0a0a' }}>
            {editingSubcategory ? 'Edit Subcategory' : 'Add Subcategory'}
          </h3>
          <button onClick={onClose} style={{ 
            width: '32px', height: '32px', borderRadius: '50%', background: '#f1f5f9', 
            border: 'none', cursor: 'pointer' 
          }}>✕</button>
        </div>
        <input 
          type="text" 
          value={name} 
          onChange={(e) => setName(e.target.value)} 
          placeholder="Subcategory name" 
          style={{ 
            width: '100%', padding: '12px', marginBottom: '20px', border: '1px solid #e2e8f0', 
            borderRadius: '8px', fontSize: '14px' 
          }} 
          autoFocus 
        />
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={onClose} style={{ 
            flex: 1, padding: '10px', background: '#f1f5f9', borderRadius: '8px', 
            cursor: 'pointer', fontWeight: 500 
          }}>Cancel</button>
          <button onClick={handleSubmit} disabled={loading} style={{ 
            flex: 1, padding: '10px', background: loading ? '#94a3b8' : '#dc2626', 
            color: 'white', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer', 
            fontWeight: 600 
          }}>
            {loading ? 'Saving...' : (editingSubcategory ? 'Update' : 'Add')}
          </button>
        </div>
      </div>
    </>
  );
};

// Search Suggestions Component
const SearchSuggestions = ({ searchTerm, onSelect, onClose, products }) => {
  const suggestions = useMemo(() => {
    if (!searchTerm.trim()) return [];
    const term = searchTerm.toLowerCase();
    const productsArray = Array.isArray(products) ? products : [];
    return productsArray.filter(p => p && (p.name?.toLowerCase().includes(term) || p.tag?.toLowerCase().includes(term))).slice(0, 5);
  }, [searchTerm, products]);
  
  if (!searchTerm || suggestions.length === 0) return null;
  
  return (
    <div style={{
      position: 'absolute',
      top: 'calc(100% + 4px)',
      left: 0,
      right: 0,
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
      zIndex: 1001,
      maxHeight: 350,
      overflow: 'auto',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ padding: '12px' }}>
        <div style={{ padding: '0 8px', marginBottom: '8px', fontSize: '11px', fontWeight: 600, color: '#dc2626' }}>
          Suggestions
        </div>
        {suggestions.map(p => p && (
          <button
            key={p.id}
            onClick={() => onSelect(p.name)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              padding: '8px 12px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '8px',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#fff5f5'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
          >
            <img src={p.image} alt={p.name} style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 500 }}>{p.name}</div>
              <div style={{ fontSize: '11px', color: '#dc2626' }}>₹{(p.price || 0).toLocaleString()}</div>
            </div>
            <Search size={14} color="#dc2626" />
          </button>
        ))}
      </div>
    </div>
  );
};

// Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange, isMobile }) => {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = isMobile ? 3 : 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };
  
  if (totalPages <= 1) return null;
  
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '6px',
      marginTop: '24px',
      padding: '16px 0',
      flexWrap: 'wrap'
    }}>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        style={{
          padding: '6px 10px',
          borderRadius: '6px',
          border: '1px solid #e2e8f0',
          background: 'white',
          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
          opacity: currentPage === 1 ? 0.5 : 1,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: '12px'
        }}
      >
        <ChevronLeft size={14} /> Prev
      </button>
      
      {getPageNumbers().map((page, idx) => (
        <button
          key={idx}
          onClick={() => typeof page === 'number' && onPageChange(page)}
          style={{
            padding: '6px 12px',
            borderRadius: '6px',
            border: currentPage === page ? 'none' : '1px solid #e2e8f0',
            background: currentPage === page ? '#dc2626' : 'white',
            color: currentPage === page ? 'white' : '#475569',
            cursor: typeof page === 'number' ? 'pointer' : 'default',
            fontWeight: currentPage === page ? 600 : 400,
            minWidth: '32px'
          }}
        >
          {page}
        </button>
      ))}
      
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        style={{
          padding: '6px 10px',
          borderRadius: '6px',
          border: '1px solid #e2e8f0',
          background: 'white',
          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
          opacity: currentPage === totalPages ? 0.5 : 1,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: '12px'
        }}
      >
        Next <ChevronRight size={14} />
      </button>
    </div>
  );
};

// Main ProductManager Component
const ProductManager = ({ products = [], loading, onAdd, onUpdate, onDelete, onToggleStock }) => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [editProduct, setEditProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const itemsPerPage = isMobile ? 6 : 12;
  const searchRef = useRef(null);

  // Full-screen image viewer state
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [fullScreenImages, setFullScreenImages] = useState([]);
  const [fullScreenIndex, setFullScreenIndex] = useState(0);

  // Ensure products is always an array
  const safeProducts = useMemo(() => {
    return Array.isArray(products) ? products.filter(p => p && typeof p === 'object') : [];
  }, [products]);

  // Load categories & subcategories
  useEffect(() => {
    const loadData = async () => {
      try {
        let fbCats = await dbGet('shop_categories');
        let catsArray = [];
        
        if (fbCats && Array.isArray(fbCats)) {
          catsArray = fbCats.filter(cat => cat && typeof cat === 'object');
        } else if (fbCats && typeof fbCats === 'object') {
          catsArray = Object.entries(fbCats)
            .map(([key, value]) => ({ id: key, ...value }))
            .filter(cat => cat && typeof cat === 'object');
        } else {
          const saved = localStorage.getItem('shop_categories');
          if (saved) {
            const parsed = JSON.parse(saved);
            catsArray = Array.isArray(parsed) ? parsed.filter(cat => cat && typeof cat === 'object') : [];
          }
        }
        
        if (catsArray.length === 0) {
          catsArray = [
            { id: 'cat_1', name: 'Bouquets', icon: '💐' },
            { id: 'cat_2', name: 'Fresh Flowers', icon: '🌸' },
            { id: 'cat_3', name: 'Gift Hampers', icon: '🎁' },
            { id: 'cat_4', name: 'Plants', icon: '🪴' },
          ];
        }
        
        setCategories(catsArray);
        if (catsArray.length && !activeCategory) setActiveCategory(catsArray[0]);

        let fbSubs = await dbGet('shop_subcategories');
        let subsObj = {};
        
        if (fbSubs && typeof fbSubs === 'object') {
          subsObj = fbSubs;
        } else {
          const savedSubs = localStorage.getItem('shop_subcategories');
          if (savedSubs) subsObj = JSON.parse(savedSubs);
        }
        
        setSubcategories(subsObj);
      } catch (err) { 
        console.error('Error loading categories:', err);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle click outside for suggestions
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const saveCategories = useCallback(async (newCats) => {
    const validCats = newCats.filter(cat => cat && typeof cat === 'object');
    setCategories(validCats);
    localStorage.setItem('shop_categories', JSON.stringify(validCats));
    try {
      await dbSet('shop_categories', validCats);
    } catch (error) {
      console.error('Error saving to Firebase:', error);
    }
  }, []);

  const saveSubcategories = useCallback(async (newSubs) => {
    setSubcategories(newSubs);
    localStorage.setItem('shop_subcategories', JSON.stringify(newSubs));
    try {
      await dbSet('shop_subcategories', newSubs);
    } catch (error) {
      console.error('Error saving to Firebase:', error);
    }
  }, []);

  // Category CRUD
  const handleAddCategory = async (cat) => {
    const newCat = { id: `cat_${Date.now()}`, name: cat.name, icon: cat.icon, createdAt: new Date().toISOString() };
    await saveCategories([...categories, newCat]);
    setSubcategories(prev => ({ ...prev, [newCat.id]: [] }));
    toast.success('Category added');
    setShowCategoryModal(false);
  };

  const handleEditCategory = async (cat) => {
    const updated = categories.map(c => c.id === cat.id ? { ...c, name: cat.name, icon: cat.icon } : c);
    await saveCategories(updated);
    if (activeCategory?.id === cat.id) setActiveCategory(updated.find(c => c.id === cat.id));
    toast.success('Category updated');
    setShowCategoryModal(false);
    setEditingCategory(null);
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}" and all its subcategories?`)) return;
    const newCats = categories.filter(c => c.id !== cat.id);
    await saveCategories(newCats);
    const newSubs = { ...subcategories };
    delete newSubs[cat.id];
    await saveSubcategories(newSubs);
    if (activeCategory?.id === cat.id) setActiveCategory(newCats[0] || null);
    toast.success('Category deleted');
  };

  // Subcategory CRUD
  const handleAddSubcategory = async (sub) => {
    const newSub = { id: `sub_${Date.now()}`, name: sub.name, categoryId: sub.categoryId };
    const current = subcategories[sub.categoryId] || [];
    await saveSubcategories({ ...subcategories, [sub.categoryId]: [...current, newSub] });
    toast.success('Subcategory added');
    setShowSubcategoryModal(false);
  };

  const handleEditSubcategory = async (sub) => {
    const current = subcategories[sub.categoryId] || [];
    const updated = current.map(s => s.id === sub.id ? { ...s, name: sub.name } : s);
    await saveSubcategories({ ...subcategories, [sub.categoryId]: updated });
    toast.success('Subcategory updated');
    setShowSubcategoryModal(false);
    setEditingSubcategory(null);
  };

  const handleDeleteSubcategory = async (sub) => {
    if (!window.confirm(`Delete "${sub.name}"?`)) return;
    const current = subcategories[sub.categoryId] || [];
    const updated = current.filter(s => s.id !== sub.id);
    await saveSubcategories({ ...subcategories, [sub.categoryId]: updated });
    toast.success('Subcategory deleted');
  };

  // Toggle product stock status
  const handleToggleStock = async (product) => {
    if (!product || !product.id) return;
    if (!onToggleStock) {
      toast.error('Stock management not available');
      return;
    }
    
    const currentStockStatus = product.stock !== false && product.outOfStock !== true;
    try {
      const result = await onToggleStock(product.id, currentStockStatus);
      if (result && result.success) {
        toast.success(result.inStock ? 'Product back in stock!' : 'Product marked as out of stock');
      }
    } catch (err) {
      toast.error('Failed to update stock status');
    }
  };

  // Product helpers
  const getCategoryProductCount = (categoryId) => {
    return safeProducts.filter(p => p && (p.category === categoryId || p.mainCategory === categoryId)).length;
  };

  const getSubcategoryProductCount = (subId, subName) => {
    return safeProducts.filter(p => p && (p.subcategoryId === subId || p.subcategory === subName)).length;
  };

  // Get filtered products
  const getFilteredProducts = useCallback(() => {
    let filtered = [...safeProducts];
    
    if (activeCategory && activeCategory.id) {
      filtered = filtered.filter(p => p && (p.category === activeCategory.id || p.mainCategory === activeCategory.id));
    }
    
    if (selectedSubcategory && selectedSubcategory.id) {
      filtered = filtered.filter(p => p && (p.subcategoryId === selectedSubcategory.id || p.subcategory === selectedSubcategory.name));
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => p && (p.name?.toLowerCase().includes(term) || p.tag?.toLowerCase().includes(term)));
    }
    
    return filtered;
  }, [safeProducts, activeCategory, selectedSubcategory, searchTerm]);

  const filteredProducts = getFilteredProducts();
  const safeFilteredProducts = Array.isArray(filteredProducts) ? filteredProducts : [];
  const totalPages = Math.max(1, Math.ceil(safeFilteredProducts.length / itemsPerPage));
  const paginatedProducts = safeFilteredProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  
  const currentSubcategories = activeCategory && activeCategory.id ? (subcategories[activeCategory.id] || []) : [];
  const safeSubcategories = Array.isArray(currentSubcategories) ? currentSubcategories.filter(sub => sub && typeof sub === 'object') : [];

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, selectedSubcategory, searchTerm]);

  const handleAddProduct = (sub = null) => {
    setSelectedSubcategory(sub);
    setEditProduct(null);
    setShowForm(true);
  };

  const handleEditProduct = (product) => {
    setEditProduct(product);
    setShowForm(true);
  };

  const handleDeleteProduct = async (product) => {
    if (!product || !product.id) return;
    if (window.confirm(`Delete "${product.name}"?`)) {
      try {
        await onDelete(product.id, product.imageId);
        toast.success('Product deleted');
      } catch (err) { 
        toast.error('Delete failed: ' + err.message); 
      }
    }
  };

  const handleFormSubmit = async (productData) => {
    try {
      let final = { ...productData };
      if (selectedSubcategory) {
        final.subcategoryId = selectedSubcategory.id;
        final.subcategory = selectedSubcategory.name;
      }
      if (activeCategory) {
        final.category = activeCategory.id;
        final.mainCategory = activeCategory.id;
      }
      
      if (editProduct) {
        await onUpdate(editProduct.id, final);
        toast.success('Product updated successfully!');
      } else {
        final.createdAt = new Date().toISOString();
        await onAdd(final);
        toast.success('Product added successfully!');
      }
      setShowForm(false);
      setEditProduct(null);
      setSelectedSubcategory(null);
      setCurrentPage(1);
    } catch (err) { 
      toast.error('Operation failed: ' + err.message); 
    }
  };

  const handleSearch = (query) => {
    setSearchTerm(query);
    setShowSuggestions(false);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setShowSuggestions(false);
    setCurrentPage(1);
  };

  // Handle image click to open full-screen
  const handleImageClick = (images, index) => {
    if (images && images.length > 0) {
      setFullScreenImages(images);
      setFullScreenIndex(index);
      setShowFullScreen(true);
      document.body.style.overflow = 'hidden';
    }
  };

  // Close full-screen viewer
  const closeFullScreen = () => {
    setShowFullScreen(false);
    setFullScreenImages([]);
    setFullScreenIndex(0);
    document.body.style.overflow = 'auto';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="spinner" style={{ width: 40, height: 40, border: '3px solid #dc2626', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
      </div>
    );
  }

  const validCategories = categories.filter(cat => cat && typeof cat === 'object');

  return (
    <div style={{ paddingBottom: '80px' }}>
      {/* Full-Screen Image Viewer */}
      {showFullScreen && (
        <FullScreenImageViewer
          images={fullScreenImages}
          initialIndex={fullScreenIndex}
          onClose={closeFullScreen}
        />
      )}

      {/* Header - Cherry Theme */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px', color: '#1a0a0a' }}>Product Management</h2>
          <p style={{ color: '#64748b', fontSize: '12px' }}>Manage categories, subcategories, and products</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '4px', background: '#f1f5f9', borderRadius: '8px', padding: '3px' }}>
            <button
              onClick={() => setViewMode('grid')}
              style={{
                padding: '6px 10px',
                borderRadius: '6px',
                background: viewMode === 'grid' ? '#dc2626' : 'transparent',
                color: viewMode === 'grid' ? 'white' : '#64748b',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px'
              }}
            >
              <Grid size={14} /> Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '6px 10px',
                borderRadius: '6px',
                background: viewMode === 'list' ? '#dc2626' : 'transparent',
                color: viewMode === 'list' ? 'white' : '#64748b',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px'
              }}
            >
              <List size={14} /> List
            </button>
          </div>
          <button onClick={() => { setEditingCategory(null); setShowCategoryModal(true); }} style={{ 
            padding: '8px 16px', background: 'linear-gradient(135deg, #dc2626, #b91c1c)', 
            color: 'white', border: 'none', borderRadius: '8px', display: 'flex', 
            alignItems: 'center', gap: '6px', cursor: 'pointer', fontWeight: 500,
            boxShadow: '0 2px 8px rgba(220,38,38,0.2)'
          }}>
            <Plus size={14} /> New Category
          </button>
        </div>
      </div>

      {/* Category Grid */}
      {validCategories.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: '12px', marginBottom: '24px' }}>
          {validCategories.map(cat => (
            <CategoryCard 
              key={cat.id} 
              category={cat} 
              isActive={activeCategory?.id === cat.id} 
              onClick={() => { setActiveCategory(cat); setSelectedSubcategory(null); setSearchTerm(''); setCurrentPage(1); }}
              productCount={getCategoryProductCount(cat.id)} 
              onEdit={() => { setEditingCategory(cat); setShowCategoryModal(true); }} 
              onDelete={() => handleDeleteCategory(cat)} 
              isMobile={isMobile} 
            />
          ))}
        </div>
      )}

      {/* Subcategories section */}
      {activeCategory && (
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FolderTree size={16} color="#dc2626" />
              <h3 style={{ fontSize: '13px', fontWeight: 600, color: '#1a0a0a' }}>Subcategories in {activeCategory.name}</h3>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => { setEditingSubcategory(null); setShowSubcategoryModal(true); }} style={{ 
                padding: '6px 14px', background: '#10b981', color: 'white', borderRadius: '6px', 
                fontSize: '11px', cursor: 'pointer', border: 'none', fontWeight: 500 
              }}>
                <Plus size={12} /> New Subcategory
              </button>
              <button onClick={() => handleAddProduct(null)} style={{ 
                padding: '6px 14px', background: '#dc2626', color: 'white', borderRadius: '6px', 
                fontSize: '11px', cursor: 'pointer', border: 'none', fontWeight: 500 
              }}>
                <Plus size={12} /> Add Product
              </button>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '12px' }}>
            {safeSubcategories.map(sub => (
              <SubCategoryCard 
                key={sub.id} 
                subcategory={sub} 
                productCount={getSubcategoryProductCount(sub.id, sub.name)}
                onEdit={() => { setEditingSubcategory(sub); setShowSubcategoryModal(true); }}
                onDelete={() => handleDeleteSubcategory(sub)} 
                onAddProduct={handleAddProduct} 
                isMobile={isMobile} 
              />
            ))}
          </div>
          {safeSubcategories.length === 0 && (
            <div style={{ textAlign: 'center', padding: '30px', background: '#f8fafc', borderRadius: '10px', border: '1px dashed #e2e8f0' }}>
              <Folder size={28} color="#94a3b8" />
              <p style={{ marginTop: '8px', fontSize: '12px', color: '#64748b' }}>No subcategories yet</p>
              <button onClick={() => { setEditingSubcategory(null); setShowSubcategoryModal(true); }} style={{ 
                marginTop: '8px', padding: '6px 14px', background: '#dc2626', color: 'white', 
                borderRadius: '6px', fontSize: '11px', cursor: 'pointer', border: 'none' 
              }}>
                + Add First Subcategory
              </button>
            </div>
          )}
        </div>
      )}

      {/* Search Bar with Suggestions */}
      <div ref={searchRef} style={{ position: 'relative', marginBottom: '16px' }}>
        <div style={{ background: 'white', borderRadius: '10px', padding: '10px 12px', border: '1px solid #e2e8f0', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#dc2626' }} />
            <input 
              type="text" 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch(searchTerm)}
              placeholder="Search products by name or tag..." 
              style={{ 
                width: '100%', padding: '8px 10px 8px 32px', border: '1px solid #e2e8f0', 
                borderRadius: '8px', fontSize: '13px', outline: 'none' 
              }} 
            />
            {searchTerm && (
              <button onClick={clearSearch} style={{ 
                position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', 
                background: 'none', border: 'none', cursor: 'pointer' 
              }}>
                <X size={12} />
              </button>
            )}
          </div>
          {(searchTerm || selectedSubcategory) && (
            <button onClick={() => { setSearchTerm(''); setSelectedSubcategory(null); setCurrentPage(1); }} 
              style={{ padding: '6px 12px', background: '#f1f5f9', borderRadius: '6px', fontSize: '11px', cursor: 'pointer', border: 'none' }}>
              Clear Filters
            </button>
          )}
        </div>
        
        {/* Search Suggestions Dropdown */}
        {showSuggestions && (
          <SearchSuggestions
            searchTerm={searchTerm}
            onSelect={handleSearch}
            onClose={() => setShowSuggestions(false)}
            products={safeProducts}
          />
        )}
      </div>

      {/* Products Display */}
      {safeFilteredProducts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <Package size={40} color="#cbd5e1" />
          <h3 style={{ marginTop: '12px', fontSize: '14px', color: '#1a0a0a' }}>No Products Found</h3>
          <p style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
            {searchTerm ? 'Try a different search term' : `Add your first product to ${activeCategory?.name || 'categories'}`}
          </p>
          {activeCategory && (
            <button onClick={() => handleAddProduct(null)} style={{ 
              marginTop: '16px', padding: '8px 20px', background: '#dc2626', color: 'white', 
              borderRadius: '8px', fontSize: '12px', cursor: 'pointer', border: 'none' 
            }}>
              + Add Product
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '16px' }}>
            {paginatedProducts.map(product => (
              <ProductCardAdmin 
                key={product.id} 
                product={product} 
                onEdit={handleEditProduct} 
                onDelete={handleDeleteProduct}
                onToggleStock={handleToggleStock}
                isMobile={isMobile} 
                onImageClick={handleImageClick}
              />
            ))}
          </div>
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} isMobile={isMobile} />
        </>
      ) : (
        <>
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f8fafc' }}>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: 600, color: '#64748b', textAlign: 'center' }}>#</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: 600, color: '#64748b', textAlign: 'left' }}>Product</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: 600, color: '#64748b', textAlign: 'left' }}>Price</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: 600, color: '#64748b', textAlign: 'left' }}>Original</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: 600, color: '#64748b', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: 600, color: '#64748b', textAlign: 'left' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedProducts.map((product, idx) => (
                  <ProductRow
                    key={product.id}
                    product={product}
                    index={(currentPage - 1) * itemsPerPage + idx}
                    onEdit={handleEditProduct}
                    onDelete={handleDeleteProduct}
                    onToggleStock={handleToggleStock}
                    onImageClick={handleImageClick}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} isMobile={isMobile} />
        </>
      )}

      {/* Modals */}
      {showCategoryModal && (
        <AddCategoryModal 
          onClose={() => { setShowCategoryModal(false); setEditingCategory(null); }} 
          onSave={editingCategory ? handleEditCategory : handleAddCategory} 
          editingCategory={editingCategory} 
        />
      )}
      
      {showSubcategoryModal && activeCategory && (
        <AddSubcategoryModal 
          onClose={() => { setShowSubcategoryModal(false); setEditingSubcategory(null); }} 
          onSave={editingSubcategory ? handleEditSubcategory : handleAddSubcategory} 
          categoryId={activeCategory.id} 
          editingSubcategory={editingSubcategory} 
        />
      )}
      
      {showForm && (
        <ProductForm 
          product={editProduct} 
          category={activeCategory}
          subcategories={safeSubcategories}
          selectedSubcategory={selectedSubcategory}
          onSubmit={handleFormSubmit}
          onClose={() => { setShowForm(false); setEditProduct(null); setSelectedSubcategory(null); }} 
        />
      )}

      <style>{`
        .category-actions { opacity: 0; }
        div:hover > .category-actions { opacity: 1; }
        .spinner { animation: spin 0.6s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default ProductManager;